import { useTheme } from '../../context/ThemeContext';
import { Tooltip } from '../../ui';
import { ProbeStatus, type ProviderProbeResult } from '../../gql/operations';

// Inline result chip for a provider connection probe (W3b). Maps each ProbeStatus onto a semantic
// tone (success / warn / danger) and a short label, appends latency for OK, and surfaces the
// server `message` on hover (Tooltip) — both themes (DESIGN_LANGUAGE_SPEC §3 semantic color).

type Tone = 'success' | 'warn' | 'danger';

interface StatusMeta {
  tone: Tone;
  label: string;
}

const STATUS_META: Record<ProbeStatus, StatusMeta> = {
  [ProbeStatus.Ok]: { tone: 'success', label: 'OK' },
  [ProbeStatus.AuthFailed]: { tone: 'danger', label: 'Auth failed' },
  [ProbeStatus.Unreachable]: { tone: 'danger', label: 'Unreachable' },
  [ProbeStatus.InvalidResponse]: { tone: 'warn', label: 'Invalid response' },
  [ProbeStatus.NoCredentials]: { tone: 'warn', label: 'No credentials' },
  // 2026-07-04: upstream is up + speaking the protocol, but none of the provider's
  // enabled aliases match what the upstream serves — every real dispatch would 400
  // with `model_not_found`. Danger tone so the card actually reads red.
  [ProbeStatus.ModelMismatch]: { tone: 'danger', label: 'Model mismatch' },
};

const TONE_STYLES: Record<Tone, { dark: string; light: string }> = {
  success: {
    dark: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300',
    light: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  warn: {
    dark: 'border-amber-500/25 bg-amber-500/10 text-amber-200',
    light: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  danger: {
    dark: 'border-rose-500/25 bg-rose-500/10 text-rose-300',
    light: 'border-rose-200 bg-rose-50 text-rose-700',
  },
};

const TONE_DOT: Record<Tone, string> = {
  success: 'bg-emerald-400',
  warn: 'bg-amber-400',
  danger: 'bg-rose-400',
};

export interface ProbeResultBadgeProps {
  result: ProviderProbeResult;
}

export function ProbeResultBadge({ result }: ProbeResultBadgeProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const meta = STATUS_META[result.status];
  const styles = TONE_STYLES[meta.tone];

  const latencySuffix =
    result.status === ProbeStatus.Ok && result.latencyMs != null
      ? ` · ${result.latencyMs} ms`
      : '';
  const text = `${meta.label}${latencySuffix}`;

  // The message is shown inline (small print) AND on hover of the chip for the full text.
  const chip = (
    <span
      data-testid="probe-result"
      data-status={result.status}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
        isDark ? styles.dark : styles.light
      }`}
    >
      <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${TONE_DOT[meta.tone]}`} aria-hidden="true" />
      {text}
    </span>
  );

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-2">
      {result.message ? (
        <Tooltip content={result.message} side="top">
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
      {result.message && (
        <span
          className={`truncate text-[11px] ${isDark ? 'text-white/40' : 'text-slate-500'}`}
          title={result.message}
        >
          {result.message}
        </span>
      )}
    </span>
  );
}
